import { useEffect, useRef, useState } from 'react';

import { Button, Card, MonoLabel } from '@/design-system';
import type { ReportApproval } from '@/entities/report';
import { formatDayLong } from '@/shared/lib';

import { copyText } from '../browser';
import { reportCopy } from '../copy';
import { invoiceResultLine } from '../invoiceResult';

export interface ApprovalResultProps {
    approval: ReportApproval;
    /**
     * `resend`: opnieuw versturen moest een nieuwe link maken. Dan zegt de kop
     * dat, en staat de nieuwe code er net zo één keer in als na goedkeuren.
     */
    kind?: 'approve' | 'resend';
    /** Er was gevraagd de factuur mee te sturen: dan zegt het blok ook of dat gebeurde. */
    invoiceRequested?: boolean;
    onClose: () => void;
}

/**
 * Wat goedkeuren opleverde: bij wie het aankwam, de link en - als het bedrijf
 * er een vraagt - de toegangscode. Koos kantoor voor "alleen vaststellen", dan
 * staat er dat er niets verstuurd is, met de link om zelf mee te sturen. Was het
 * een nieuwe versie van een rapport dat de klant al had, dan staat er dat hij die
 * via dezelfde link ziet, en of hij er een bericht over kreeg.
 *
 * De code bestaat alleen in het antwoord op goedkeuren. Hij staat daarom alleen
 * in het geheugen van dit blok; sluiten is definitief, en dat staat erbij.
 */
export function ApprovalResult({
    approval,
    kind = 'approve',
    invoiceRequested = false,
    onClose,
}: ApprovalResultProps) {
    const headingRef = useRef<HTMLHeadingElement>(null);
    const { delivery, link } = approval;
    const invoiceLine = invoiceResultLine(approval.invoice, invoiceRequested);
    const channels = delivery.channels
        .map((channel) => reportCopy.channels[channel] ?? channel)
        .join(' en ');
    const withoutPhone = approval.report.recipients
        .filter((recipient) => recipient.email !== null && !recipient.phone)
        .map((recipient) => recipient.name);
    /* Een nieuwe versie achter de link die de klant al heeft. */
    const correction = kind === 'approve' && approval.report.replacesVersion !== null;
    /* Vastgesteld zonder te versturen: er ging niets uit, en dat moet er staan. */
    const released =
        kind === 'approve' && !correction && delivery.sent === 0 && delivery.failed === 0;

    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    return (
        <Card
            variant="accent"
            padding="lg"
            role="region"
            aria-labelledby="rapport-verstuurd"
            className="mb-[14px]"
        >
            <h2
                id="rapport-verstuurd"
                ref={headingRef}
                tabIndex={-1}
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] font-semibold leading-[1.3] text-[var(--text-primary)] outline-none"
            >
                {kind === 'resend'
                    ? reportCopy.resultResentTitle
                    : delivery.failed > 0
                      ? reportCopy.resultPartialTitle
                      : correction
                        ? reportCopy.resultCorrectionTitle(approval.report.version)
                        : released
                          ? reportCopy.resultReleasedTitle
                          : reportCopy.resultSentTitle}
            </h2>
            {released ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {reportCopy.resultReleased}
                </p>
            ) : null}
            {correction ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {delivery.sent > 0
                        ? reportCopy.resultCorrectionNotified(delivery.sent)
                        : reportCopy.resultCorrectionQuiet}
                </p>
            ) : delivery.sent > 0 ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {reportCopy.resultSent(delivery.sent, channels)}
                </p>
            ) : null}
            {invoiceLine ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {invoiceLine}
                </p>
            ) : null}
            {delivery.failed > 0 ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]">
                    {reportCopy.resultFailed(delivery.failed)}
                </p>
            ) : null}

            <MonoLabel size={10} tone="muted" as="div" className="mt-4 mb-2">
                {reportCopy.resultLink}
            </MonoLabel>
            <CopyRow value={link.url} label={reportCopy.copyLink} />
            <p className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {reportCopy.resultValidUntil(formatDayLong(link.validUntil))}
            </p>

            {link.accessCode ? (
                <>
                    <MonoLabel size={10} tone="muted" as="div" className="mt-4 mb-2">
                        {reportCopy.resultCode}
                    </MonoLabel>
                    <CopyRow value={link.accessCode} label={reportCopy.copyCode} large />
                    <div className="mt-3 rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint)] p-3">
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                            {reportCopy.resultCodeWarning}
                        </p>
                        {withoutPhone.length > 0 ? (
                            <p className="m-0 mt-2 text-[13px] leading-[1.5] font-medium text-[var(--warn-ink)]">
                                {reportCopy.resultCodeWithoutPhone(withoutPhone.join(', '))}
                            </p>
                        ) : null}
                    </div>
                </>
            ) : null}

            <div className="mt-4">
                <Button variant="outline" size="md" onClick={onClose}>
                    {reportCopy.resultClose}
                </Button>
            </div>
        </Card>
    );
}

/** Een waarde om over te nemen, met een knop die zegt of het kopiëren lukte. */
function CopyRow({
    value,
    label,
    large = false,
}: {
    value: string;
    label: string;
    large?: boolean;
}) {
    const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2">
                <output
                    className={
                        large
                            ? 'min-w-0 rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 font-[family-name:var(--font-mono)] text-[24px] leading-[1.3] tracking-[0.12em] text-[var(--text-primary)] select-all'
                            : 'min-w-0 flex-[1_1_260px] break-all rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-[10px] font-[family-name:var(--font-mono)] text-[12px] leading-[1.45] text-[var(--text-primary)] select-all'
                    }
                >
                    {value}
                </output>
                <Button
                    variant="outline"
                    size="md"
                    onClick={async () => setState((await copyText(value)) ? 'copied' : 'failed')}
                >
                    {state === 'copied' ? reportCopy.copied : label}
                </Button>
            </div>
            {state === 'failed' ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--warn-ink)]"
                >
                    {reportCopy.copyFailed}
                </p>
            ) : null}
        </div>
    );
}
